import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { UserCheck, AlertCircle, CheckCircle } from 'lucide-react';
import PasswordStrengthIndicator from '@/components/PasswordStrengthIndicator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AcceptInvitationPage = ({ onLogin }) => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    new_password: '',
    confirm_password: '',
  });
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [passwordValidation, setPasswordValidation] = useState({
    score: 0,
    strength: 'Très faible',
    is_valid: false,
    issues: []
  });
  const [tokenValid, setTokenValid] = useState(false);
  const [invitationInfo, setInvitationInfo] = useState(null);

  // Validation directement dans useEffect pour éviter boucles

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!formData.new_password) {
        setPasswordValidation({
          score: 0,
          strength: 'Très faible',
          is_valid: false,
          issues: []
        });
        return;
      }

      // Validation directe sans dépendance fonction
      const validatePasswordDirect = async () => {
        try {
          const response = await axios.post(`${API}/auth/validate-password`, { password: formData.new_password });
          setPasswordValidation(response.data);
        } catch (error) {
          console.error('Password validation error:', error);
        }
      };

      validatePasswordDirect();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [formData.new_password]);

  // Validate invitation token on page load
  useEffect(() => {
    const validateInvitation = async () => {
      if (!token) {
        toast.error('Token d\'invitation manquant');
        navigate('/login');
        return;
      }

      try {
        // We'll check if the token is valid by making a test request
        // In a real implementation, you might have a dedicated endpoint for this
        setTokenValid(true);
        setInvitationInfo({
          message: 'Invitation valide'
        });
      } catch (error) {
        console.error('Token validation error:', error);
        toast.error('Invitation invalide ou expirée');
        setTimeout(() => navigate('/login'), 2000);
      } finally {
        setValidating(false);
      }
    };

    validateInvitation();
  }, [token, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!formData.new_password || !formData.confirm_password) {
      toast.error('Veuillez remplir tous les champs');
      return false;
    }

    if (formData.new_password !== formData.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return false;
    }

    if (!passwordValidation.is_valid) {
      toast.error('Le mot de passe ne respecte pas les critères de sécurité');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/accept-invitation/${token}`, {
        token: token,
        new_password: formData.new_password
      });

      toast.success(response.data.message || 'Invitation acceptée avec succès !');
      
      // Auto-login the user
      if (response.data.access_token && response.data.user) {
        onLogin(response.data.access_token, response.data.user);
        navigate('/');
      } else {
        navigate('/login');
      }
    } catch (error) {
      console.error('Accept invitation error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'acceptation de l\'invitation');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-black p-4">
        <Card className="w-full max-w-md shadow-2xl border-gray-200 dark:border-gray-800 dark:border-gray-800">
          <CardContent className="pt-6 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-gray-200 dark:border-gray-800 border-t-slate-600 rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Vérification de l'invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-black p-4">
        <Card className="w-full max-w-md shadow-2xl border-red-100">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Invitation invalide</h2>
            <p className="text-gray-600 dark:text-gray-400">
              Cette invitation n'est plus valide ou a déjà été utilisée.
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="w-full bg-slate-800 text-white dark:bg-slate-700"
            >
              Retour à la connexion
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-black p-4">
      <Card className="w-full max-w-lg shadow-2xl border-gray-200 dark:border-gray-800 dark:border-gray-800">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto w-16 h-16 bg-slate-800 dark:bg-slate-700 rounded-2xl flex items-center justify-center">
            <UserCheck className="w-9 h-9 text-white" />
          </div>
          <CardTitle className="text-3xl font-bold text-gray-900 dark:text-white">Firma</CardTitle>
          <CardDescription className="text-base">
            Accepter votre invitation et créer votre mot de passe
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 text-blue-800">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Invitation valide</span>
            </div>
            <p className="text-blue-700 text-sm mt-1">
              Créez un mot de passe sécurisé pour finaliser votre compte.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="accept-invitation-form">
            {/* New Password field */}
            <div className="space-y-2">
              <Label htmlFor="new_password">Nouveau mot de passe *</Label>
              <Input
                id="new_password"
                name="new_password"
                type="password"
                placeholder="Votre nouveau mot de passe"
                value={formData.new_password}
                onChange={handleInputChange}
                required
                data-testid="new-password-input"
                className="border-gray-200 dark:border-gray-800 focus:border-slate-500"
              />
              <PasswordStrengthIndicator 
                password={formData.new_password} 
                validation={passwordValidation} 
              />
            </div>

            {/* Confirm Password field */}
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirmer le mot de passe *</Label>
              <Input
                id="confirm_password"
                name="confirm_password"
                type="password"
                placeholder="Retapez votre mot de passe"
                value={formData.confirm_password}
                onChange={handleInputChange}
                required
                data-testid="confirm-password-input"
                className={`border-gray-200 dark:border-gray-800 focus:border-slate-500 ${
                  formData.confirm_password && formData.new_password !== formData.confirm_password
                    ? 'border-red-500 bg-red-50'
                    : formData.confirm_password && formData.new_password === formData.confirm_password
                    ? 'border-green-500 bg-green-50'
                    : ''
                }`}
              />
              {formData.confirm_password && formData.new_password !== formData.confirm_password && (
                <p className="text-xs text-red-600">Les mots de passe ne correspondent pas</p>
              )}
            </div>

            {/* Important notice */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div className="text-amber-800 text-sm">
                  <p className="font-medium">Important</p>
                  <p>Une fois votre compte activé, vous pourrez vous connecter avec ce mot de passe et inviter d'autres utilisateurs à rejoindre votre organisation.</p>
                </div>
              </div>
            </div>

            {/* Submit button */}
            <Button
              type="submit"
              disabled={loading || !passwordValidation.is_valid}
              data-testid="accept-invitation-button"
              className="w-full bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white font-medium py-3 text-base disabled:opacity-50"
            >
              {loading ? 'Activation en cours...' : 'Activer mon compte'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptInvitationPage;